import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getCurrencySymbol } from './currency';
import {
  applyPdfUnicodeFont,
  pdfTableFontStyles,
  PDF_FONT_FAMILY,
  toPdfVisualText,
  toPdfVisualRow,
} from './pdfFonts';

/**
 * Exports a table to a PDF file with an optional bold total row and an optional
 * balance summary section appended below the main table (for checks).
 */
export async function exportItemTableToPdf({
  title,
  subtitle,
  filename,
  columns,
  rows,
  totalRow,
  balanceSummary,
  balanceSummaryRows,
  currencyCode,
  generatedMeta,
  balanceSummaryTitle,
  amountColumnHeader,
  amountColumnHeaderSecondary,
  exchangeRateFooterTitle,
  exchangeRateFooterLines,
}) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
  await applyPdfUnicodeFont(doc);

  // jsPDF/autoTable have no bidi support: rewrite any Hebrew/Arabic text into the
  // visual order needed for correct left-to-right glyph drawing before it's used.
  title = toPdfVisualText(title);
  subtitle = subtitle != null ? toPdfVisualText(subtitle) : subtitle;
  generatedMeta = generatedMeta != null ? toPdfVisualText(generatedMeta) : generatedMeta;
  columns = (columns || []).map(toPdfVisualText);
  rows = (rows || []).map(toPdfVisualRow);
  totalRow = totalRow ? toPdfVisualRow(totalRow) : totalRow;
  balanceSummaryRows = balanceSummaryRows ? balanceSummaryRows.map(toPdfVisualRow) : balanceSummaryRows;
  balanceSummaryTitle = balanceSummaryTitle != null ? toPdfVisualText(balanceSummaryTitle) : balanceSummaryTitle;
  amountColumnHeader = amountColumnHeader != null ? toPdfVisualText(amountColumnHeader) : amountColumnHeader;
  amountColumnHeaderSecondary = amountColumnHeaderSecondary != null
    ? toPdfVisualText(amountColumnHeaderSecondary)
    : amountColumnHeaderSecondary;
  exchangeRateFooterTitle = exchangeRateFooterTitle != null
    ? toPdfVisualText(exchangeRateFooterTitle)
    : exchangeRateFooterTitle;
  exchangeRateFooterLines = exchangeRateFooterLines
    ? exchangeRateFooterLines.map(toPdfVisualText)
    : exchangeRateFooterLines;

  const pageWidth = doc.internal.pageSize.getWidth();
  const marginLeft = 30;
  const now = new Date().toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  const currencyLabel = currencyCode ? getCurrencySymbol(currencyCode) : null;

  doc.setFontSize(14);
  doc.setFont(PDF_FONT_FAMILY, 'bold');
  doc.text(title, marginLeft, 36);

  doc.setFontSize(9);
  doc.setFont(PDF_FONT_FAMILY, 'normal');
  doc.setTextColor(120);
  const meta = generatedMeta
    || (currencyLabel ? `Generated: ${now} · ${currencyLabel}` : `Generated: ${now}`);
  doc.text(meta, pageWidth - marginLeft, 36, { align: 'right' });
  doc.setTextColor(0);

  let tableStartY = 50;
  if (subtitle) {
    doc.setFontSize(10);
    doc.setFont(PDF_FONT_FAMILY, 'normal');
    doc.setTextColor(60);
    const subtitleLines = doc.splitTextToSize(String(subtitle), pageWidth - marginLeft * 2);
    doc.text(subtitleLines, marginLeft, 48);
    tableStartY = 48 + subtitleLines.length * 12 + 8;
    doc.setTextColor(0);
  }

  const tableFont = pdfTableFontStyles({ fontSize: 8, cellPadding: 4 });

  autoTable(doc, {
    startY: tableStartY,
    head: [columns],
    body: rows,
    foot: totalRow ? [totalRow] : undefined,
    margin: { left: marginLeft, right: marginLeft },
    styles: tableFont,
    headStyles: pdfTableFontStyles({ fillColor: [41, 98, 255], textColor: 255, fontStyle: 'bold' }),
    footStyles: pdfTableFontStyles({ fillColor: [230, 236, 255], textColor: 30, fontStyle: 'bold' }),
    alternateRowStyles: { fillColor: [248, 249, 255] },
  });

  const summaryRows = balanceSummaryRows || (balanceSummary ? [
    ['Total Items', `$${Number(balanceSummary.totalItems || 0).toLocaleString()}`],
    ['Payments', `$${Number(balanceSummary.payments || 0).toLocaleString()}`],
    ['Amount Due', `$${Number(balanceSummary.amountDue || 0).toLocaleString()}`],
  ] : null);

  if (summaryRows?.length) {
    const afterY = doc.lastAutoTable.finalY + 16;
    const dualAmount = amountColumnHeaderSecondary && summaryRows[0]?.length === 3;

    doc.setFontSize(10);
    doc.setFont(PDF_FONT_FAMILY, 'bold');
    doc.text(balanceSummaryTitle || 'Balance Summary', marginLeft, afterY);

    const summaryHead = dualAmount
      ? [['', amountColumnHeader || 'Amount', amountColumnHeaderSecondary]]
      : [['', amountColumnHeader || 'Amount']];

    autoTable(doc, {
      startY: afterY + 8,
      head: summaryHead,
      body: summaryRows,
      margin: { left: marginLeft, right: marginLeft },
      tableWidth: dualAmount ? 320 : 260,
      styles: tableFont,
      headStyles: pdfTableFontStyles({ fillColor: [80, 80, 80], textColor: 255, fontStyle: 'bold' }),
      alternateRowStyles: { fillColor: [248, 249, 255] },
    });
  }

  if (exchangeRateFooterLines?.length) {
    let footerY = (doc.lastAutoTable?.finalY ?? 50) + 16;
    doc.setFontSize(8);
    doc.setFont(PDF_FONT_FAMILY, 'bold');
    doc.setTextColor(80);
    if (exchangeRateFooterTitle) {
      doc.text(exchangeRateFooterTitle, marginLeft, footerY);
      footerY += 12;
    }
    doc.setFont(PDF_FONT_FAMILY, 'normal');
    exchangeRateFooterLines.forEach((line) => {
      doc.text(line, marginLeft, footerY);
      footerY += 11;
    });
    doc.setTextColor(0);
  }

  doc.save(filename);
}
